import { CommonModule } from '@angular/common';
import { Component, ContentChild, ElementRef, forwardRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, TemplateRef, ViewChild, ChangeDetectorRef } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ErrorHandlerService } from '@shared/services';
import { PaginationRequest } from '@shared/services';
import { MultiSelectModule } from 'primeng/multiselect';
import { TagModule } from 'primeng/tag';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { GenericTagComponent } from '@shared/components/generic-tag/generic-tag.component';
import { StopOnOptionSelectPropagationMultiSelectDirective, MultiSelectPreserveSelectionDirective } from '@shared/directives/generic-multiselect.directive';

/**
 * Data loading mode - determines how data is fetched from the API
 */
export enum DataLoadMode {
    /** Load all data at once in a single request */
    GET_ALL = 'getAll',
    /** Load data in pages using pagination */
    GET_PAGINATED = 'getPaginated'
}

/**
 * Filtering mode - determines where filtering happens
 */
export enum FilterMode {
    /** Filter on client-side using loaded data */
    CLIENT_SIDE = 'client',
    /** Filter on server-side by sending filter to API */
    SERVER_SIDE = 'server'
}

/**
 * Generic MultiSelect Component - Modular Version with Dependency Tracking
 *
 * This component provides automatic dependency tracking and reloading capabilities for multi-select dropdowns.
 *
 * Key features:
 * - Automatic dependency tracking with [dependencyValue] input
 * - Config-based dependency management with onDependencyChange callback
 * - Automatic data reloading when dependencies change
 * - Automatic selection clearing when dependencies change
 * - Public methods for manual control (reloadData, clearData)
 * - Support for multiple selection
 *
 * Basic usage:
 * ```typescript
 * const config: MultiSelectConfig = {
 *   service: skillApiService,
 *   method: 'getOptions',
 *   idField: 'id',
 *   labelFormatter: (item) => item.name,
 *   valueField: 'id',
 *   placeholder: 'Select skills'
 * };
 * ```
 *
 * Dependency tracking usage:
 * ```html
 * <generic-p-multiselect
 *   [config]="skillsConfig"
 *   [dependencyValue]="form.get('categoryId')?.value"
 *   formControlName="skillIds" />
 * ```
 */

export interface MultiSelectConfig<T = any> {
    // === REQUIRED: Data Loading Strategy ===
    /**
     * How data should be loaded from the API (REQUIRED)
     * - GET_ALL: Fetch all data at once (no pagination)
     * - GET_PAGINATED: Fetch data in pages using pagination
     */
    dataLoadMode: DataLoadMode;

    /**
     * How filtering should be performed (REQUIRED for GET_PAGINATED)
     * - CLIENT_SIDE: Filter locally on loaded data
     * - SERVER_SIDE: Send filter to server with each request
     */
    filterMode?: FilterMode;

    // === API-based configuration ===
    service?: any; // The service to call
    method?: string; // Method name to call on the service
    labelFormatter?: (item: T) => string; // How to format the label
    valueField?: string; // Which field to use as value
    searchFields?: string[]; // Fields to search in
    sortField?: string; // Default sort field
    placeholder?: string;
    // Show a custom placeholder substitute template instead of placeholder text
    placeholderSubstitute?: boolean;

    // Static options configuration (alternative to API-based)
    staticOptions?: any[]; // Array of static options
    optionLabel?: string; // Property name for label (e.g., 'label', 'name')
    optionValue?: string; // Property name for value (e.g., 'value', 'id')

    // Query configuration
    baseQuery?: any; // Static query that's always applied (e.g., departmentId filter)
    queryBuilder?: (searchTerm: string, baseQuery?: any) => any; // Custom query builder
    additionalParams?: any; // Additional parameters to pass to the service
    transformResponse?: (response: any) => any; // Transform the response before processing

    // Template name for built-in templates
    templateName?: string; // Name of the built-in template to use

    // Simple ID field name for handling pre-selected values (defaults to 'id')
    idField?: string; // Field name to use when querying by ID (e.g., 'id', '_id', 'code')

    // Support for POST requests with body data
    usePostMethod?: boolean; // Whether to use POST method with body data instead of GET with query params
    bodyBuilder?: (searchTerm: string, baseQuery?: any, idQuery?: any) => any; // Custom body builder for POST requests

    // Dependency tracking for automatic reloading (use [dependencyValue] input)
    // Callback to update baseQuery when dependency changes
    onDependencyChange?: (dependencyValue: any) => any;
}

@Component({
    selector: 'generic-p-multiselect',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        MultiSelectModule,
        TagModule,
        GenericTagComponent,
        StopOnOptionSelectPropagationMultiSelectDirective,
        MultiSelectPreserveSelectionDirective
    ],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => GenericMultiSelectComponent),
            multi: true
        }
    ],
    templateUrl: './generic-multiselect.component.html',
    styleUrls: ['./generic-multiselect.component.scss']
})
export class GenericMultiSelectComponent<T = any> implements OnInit, OnDestroy, OnChanges, ControlValueAccessor {
    @Input() id: string = '';
    @Input() placeholder: string = 'Chọn...';
    @Input() disabled: boolean = false;
    @Input() filter: boolean = true;
    // REMOVED: virtualScroll and lazy are now computed internally based on mode
    @Input() virtualScrollItemSize: number = 38;
    @Input() appendTo: string = 'body';
    @Input() styleClass: string = 'w-full';
    @Input() showClear: boolean = false;
    @Input() selectItemPerPage: number = 10;
    @Input() config!: MultiSelectConfig<T>;
    @Input() additionalFilters: any = {}; // Dynamic filters that can change
    @Input() dependencyValue: any = null; // Value that this select depends on
    @Input() display: string = 'chip'; // 'comma' or 'chip'

    @ContentChild('selectedItem') selectedItemTemplate?: TemplateRef<any>;
    @ContentChild('item') itemTemplate?: TemplateRef<any>;
    @ContentChild('placeholderSubstitute') placeholderSubstituteTemplate?: TemplateRef<any>;

    @ViewChild('genericMultiSelect', { read: ElementRef }) genericMultiSelect!: any;

    // Data for dropdown
    items: T[] = [];
    options: any[] = [];

    // Loading state
    loading: boolean = false;

    // Pagination properties
    offset: number = 0;

    // Filter property
    filterValue: string = '';

    // Static mode flag - calculated once and updated on config changes
    isStaticMode: boolean = false;

    // Debouncing for filter
    private filterSubject = new Subject<string>();

    // ControlValueAccessor properties
    value: any[] = [];
    onChange = (value: any) => { };
    onTouched = () => { };

    // Hard-coded constants
    private readonly MAX_ITEMS_FOR_VIRTUAL_SCROLL = 100;
    private readonly PAGE_SIZE = 10;
    private readonly FILTER_DEBOUNCE_TIME = 200;

    virtualScrollOptions = {
        showLoader: false,
        lazy: this.computedLazy || false,
        onLazyLoad: this.onLazyScrollLoad.bind(this)
    };

    constructor(
        private errorHandlerService: ErrorHandlerService,
        private cd: ChangeDetectorRef
    ) { }

    /**
     * Virtual scroll is computed based on mode and item count
      */
    get computedVirtualScroll(): boolean {
        if (this.isStaticMode) {
            return false; // Static options don't need virtual scroll
        }
        
        switch (this.config?.dataLoadMode) {
            case DataLoadMode.GET_ALL:
                // Enable virtual scroll if we have more than 100 items
                return this.options.length > this.MAX_ITEMS_FOR_VIRTUAL_SCROLL;
            case DataLoadMode.GET_PAGINATED:
                // Always true for paginated mode (needed for scroll handling)
                return true;
            default:
                return false;
        }
    }

    /**
     * Lazy loading is computed based on mode
      */
    get computedLazy(): boolean {
        if (this.isStaticMode) {
            return false; // Static options don't need lazy loading
        }
        
        switch (this.config?.dataLoadMode) {
            case DataLoadMode.GET_ALL:
                return false; // Never lazy load when getting all at once
            case DataLoadMode.GET_PAGINATED:
                // Always true for paginated mode (needed to load more pages)
                return true;
            default:
                return false;
        }
    }

    private updateStaticMode() {
        this.isStaticMode = !!(this.config?.staticOptions && this.config.staticOptions.length > 0);
    }

    private validateConfig() {
        const { dataLoadMode, filterMode } = this.config;

        // dataLoadMode is now REQUIRED
        if (!dataLoadMode) {
            throw new Error('MultiSelectConfig requires dataLoadMode to be specified');
        }

        // Static mode validation
        if (this.isStaticMode) {
            if (dataLoadMode !== DataLoadMode.GET_ALL) {
                throw new Error('Static options require dataLoadMode = GET_ALL');
            }
            return;
        }

        // API mode validation
        if (dataLoadMode === DataLoadMode.GET_ALL) {
            if (filterMode) {
                throw new Error('GET_ALL mode does not use filterMode');
            }
        }

        if (dataLoadMode === DataLoadMode.GET_PAGINATED) {
            if (!filterMode) {
                throw new Error('GET_PAGINATED mode requires filterMode to be specified');
            }

            if (filterMode === FilterMode.SERVER_SIDE) {
                // Virtual scroll and lazy are automatically enabled
                // No validation needed as they are computed
            }

            if (filterMode === FilterMode.CLIENT_SIDE) {
                // Client-side filtering accumulates pages
                // No validation needed
            }
        }
    }

    ngOnInit() {
        if (!this.config) {
            throw new Error('GenericMultiSelectComponent requires a config input');
        }

        // Calculate static mode
        this.updateStaticMode();
        
        // Validate configuration
        this.validateConfig();        
    }

    ngAfterViewInit() {
        // Validate configuration
        if (!this.isStaticMode && (!this.config.service || !this.config.method)) {
            throw new Error('GenericMultiSelectComponent requires either staticOptions or both service and method in config');
        }

        if (this.isStaticMode) {
            // Load static options immediately
            this.loadStaticOptions();
        } else {
            // Set up debounced filter for API mode
            this.filterSubject.pipe(
                debounceTime(this.FILTER_DEBOUNCE_TIME),
                distinctUntilChanged()
            ).subscribe(filterValue => {
                this.filterValue = filterValue;
                this.loadData(true);
            });

            // For GET_ALL mode, load all data immediately on init
            if (this.config.dataLoadMode === DataLoadMode.GET_ALL) {
                this.loadData(true);
            }
        }
    }

    /**
     * Load static options and convert them to the expected format
     */
    private loadStaticOptions() {
        if (!this.config.staticOptions) return;
        this.loading = true;

        const optionLabel = this.config.optionLabel || 'label';
        const optionValue = this.config.optionValue || 'value';

        // Use setTimeout to ensure PrimeNG is fully initialized
        setTimeout(() => {
            if (!this.config.staticOptions) return;

            this.items = [...this.config.staticOptions];
            this.options = this.config.staticOptions.map(item => ({
                ...item,
                label: item[optionLabel],
                value: item[optionValue]
            }));
            this.loading = false;
        }, 0);
    }

    ngOnChanges(changes: SimpleChanges) {
        // Handle config changes - recalculate static mode
        if (changes['config'] && !changes['config'].firstChange) {
            this.updateStaticMode();
            if (this.isStaticMode) {
                this.loadStaticOptions();
                return;
            }
        }

        // For API mode only - reload data when additionalFilters change
        if (changes['additionalFilters'] && !changes['additionalFilters'].firstChange && !this.isStaticMode) {
            this.loadData(true);
        }

        // For API mode only - reload data when dependencyValue changes
        if (changes['dependencyValue'] && !changes['dependencyValue'].firstChange && !this.isStaticMode) {
            const currentValue = changes['dependencyValue'].currentValue;
            const previousValue = changes['dependencyValue'].previousValue;

            // Only reload if the value actually changed
            if (currentValue !== previousValue) {
                // Clear current selection when dependency changes
                this.value = [];
                this.onChange([]);

                // Update baseQuery using onDependencyChange callback if available
                if (this.config.onDependencyChange) {
                    this.config.baseQuery = this.config.onDependencyChange(currentValue);
                }

                // Reload data if dependency has a value, otherwise clear data
                if (currentValue !== null && currentValue !== undefined) {
                    this.loadData(true);
                } else {
                    this.items = [];
                    this.options = [];
                }
            }
        }

        // For API mode only - reload data when config changes (for cases where config object is reassigned)
        if (changes['config'] && !changes['config'].firstChange && !this.isStaticMode) {
            this.loadData(true);
        }
    }

    ngOnDestroy() {
        this.filterSubject.complete();
    }

    loadData(resetData: boolean = false) {
        // If in static mode, don't load from API
        if (this.isStaticMode) {
            this.loadStaticOptions();
            return;
        }

        this.loading = true;

        if (resetData) {
            this.items = [];
            this.options = [];
            this.offset = 0;
        }

        // Build query using custom query builder or default logic
        let query = {};
        let body = null;

        if (this.config.usePostMethod && this.config.bodyBuilder) {
            // Use custom body builder for POST requests (no ID query for regular data loading)
            body = this.config.bodyBuilder(this.filterValue, {
                ...this.config.baseQuery,
                ...this.additionalFilters
            }, null);
        } else if (this.config.queryBuilder) {
            // Use custom query builder
            query = this.config.queryBuilder(this.filterValue, {
                ...this.config.baseQuery,
                ...this.additionalFilters
            });
        } else {
            // Default query building logic
            const baseQuery = { ...this.config.baseQuery, ...this.additionalFilters };

            if (this.filterValue && this.filterValue.trim()) {
                query = {
                    ...baseQuery,
                    search: this.filterValue.trim()
                };
            } else if (Object.keys(baseQuery).length > 0) {
                query = baseQuery;
            }
        }

        const params: PaginationRequest = {
            limit: this.selectItemPerPage,
            offset: this.offset,
            sort: this.config.sortField || 'code,1',
            query: this.config.usePostMethod ? null : query,
            ...this.config.additionalParams
        };

        // Call the service method dynamically
        if (!this.config.service || !this.config.method) {
            console.error('Service and method are required for API mode');
            this.loading = false;
            return;
        }

        const serviceMethod = this.config.service[this.config.method];
        if (!serviceMethod) {
            console.error(`Method ${this.config.method} not found on service`);
            this.loading = false;
            return;
        }

        // Call service method with or without body based on configuration
        const serviceCall = this.config.usePostMethod
            ? serviceMethod.call(this.config.service, params, body)
            : serviceMethod.call(this.config.service, params);

        serviceCall.subscribe({
            next: (response: any) => {
                // Apply response transformation if provided
                const transformedResponse = this.config.transformResponse
                    ? this.config.transformResponse(response)
                    : response;

                const { hasError, data } = this.errorHandlerService.handleInternal(transformedResponse);
                if (!hasError) {
                    if (resetData) {
                        this.items = data.items || [];
                    } else {
                        this.items = [...this.items, ...(data.items || [])]
                            .filter((item: any, index, self) => index === self.findIndex((i: any) => i.id === item.id));
                    }

                    this.options = this.items.map(item => ({
                        ...item,
                        label: this.config.labelFormatter ? this.config.labelFormatter(item) : (item as any).label || (item as any).name,
                        value: this.config.valueField ? (item as any)[this.config.valueField] : (item as any).value || (item as any).id
                    }));
                    this.cd.detectChanges();

                    this.loading = false;
                }
            },
            error: () => {
                this.loading = false;
            }
        });
    }

    onFilter(event: any) {
        const filterValue = event.filter || '';

        if (this.isStaticMode) {
            // Client-side filtering for static options
            this.filterStaticOptions(filterValue);
        } else {
            // Determine filtering behavior based on mode
            if (this.config.dataLoadMode === DataLoadMode.GET_ALL) {
                // Client-side filtering for GET_ALL mode
                this.filterStaticOptions(filterValue);
            } else if (this.config.dataLoadMode === DataLoadMode.GET_PAGINATED) {
                if (this.config.filterMode === FilterMode.CLIENT_SIDE) {
                    // Client-side filtering for GET_PAGINATED + CLIENT_SIDE mode
                    this.filterStaticOptions(filterValue);
                } else if (this.config.filterMode === FilterMode.SERVER_SIDE) {
                    // Server-side filtering for GET_PAGINATED + SERVER_SIDE mode
                    this.filterSubject.next(filterValue);
                }
            }
        }
    }

    onLazyScrollLoad(event: any) {
        // Skip lazy loading for static mode
        if (this.isStaticMode) {
            return;
        }

        // Only handle lazy loading for GET_PAGINATED mode
        if (this.config.dataLoadMode !== DataLoadMode.GET_PAGINATED) {
            return;
        }

        const first = event.first ?? 0;
        if (first === this.offset && this.options.length > 0) {
            return;
        }

        this.offset = event.first || 0;
        
        // For CLIENT_SIDE filtering, load next page without filter
        // For SERVER_SIDE filtering, load next page with current filter
        if (this.config.filterMode === FilterMode.CLIENT_SIDE) {
            // Clear filter value for next page load (filtering is done locally)
            const currentFilter = this.filterValue;
            this.filterValue = '';
            this.loadData();
            this.filterValue = currentFilter;
        } else {
            this.loadData();
        }
    }

    /**
     * Filter static options on the client side
     */
    private filterStaticOptions(filterValue: string) {
        if (!this.config.staticOptions) return;

        const optionLabel = this.config.optionLabel || 'label';

        if (!filterValue.trim()) {
            // Show all options when no filter
            this.loadStaticOptions();
        } else {
            // Filter options based on label
            const filteredOptions = this.config.staticOptions.filter(item =>
                item[optionLabel]?.toLowerCase().includes(filterValue.toLowerCase())
            );

            const optionValue = this.config.optionValue || 'value';
            this.items = [...filteredOptions];
            this.options = filteredOptions.map(item => ({
                ...item,
                label: item[optionLabel],
                value: item[optionValue]
            }));
        }
    }

    loadSpecificItems(itemIds: any[]): Promise<void> {
        return new Promise<void>((resolve) => {
            if (!itemIds || itemIds.length === 0) {
                resolve();
                return;
            }

            // If in static mode, find the items in static options
            if (this.isStaticMode) {
                const optionValue = this.config.optionValue || 'value';
                const optionLabel = this.config.optionLabel || 'label';

                itemIds.forEach(itemId => {
                    const existingOption = this.options.find(option => option.value === itemId);
                    if (!existingOption) {
                        const staticItem = this.config.staticOptions?.find(item => item[optionValue] === itemId);
                        if (staticItem) {
                            const newOption = {
                                ...staticItem,
                                label: staticItem[optionLabel],
                                value: staticItem[optionValue]
                            };
                            this.items.unshift(staticItem);
                            this.options.unshift(newOption);
                        }
                    }
                });
                resolve();
                return;
            }

            // Use the idField (defaults to 'id') to build a query for multiple IDs
            const idFieldName = this.config.idField || 'id';
            const idQuery = { [idFieldName]: itemIds };
            let body = null;
            let query = idQuery;

            if (this.config.usePostMethod && this.config.bodyBuilder) {
                // Use custom body builder for POST requests with ID query
                // Pass baseQuery so that bodyBuilder can include necessary context (e.g., courseCode)
                body = this.config.bodyBuilder('', { ...this.config.baseQuery }, idQuery);
                query = {};
            }

            const params: PaginationRequest = {
                limit: itemIds.length,
                offset: 0,
                sort: this.config.sortField || 'code,1',
                ...this.config.additionalParams
            };

            if (query) {
                params.query = query;
            }

            // Use the same service method as regular data loading
            if (!this.config.service || !this.config.method) {
                console.error('Service and method are required for API mode');
                this.loadData(true);
                resolve();
                return;
            }

            const serviceMethod = this.config.service[this.config.method];
            if (!serviceMethod) {
                console.error(`Method ${this.config.method} not found on service`);
                this.loadData(true);
                resolve();
                return;
            }

            // Call service method with or without body based on configuration
            const serviceCall = this.config.usePostMethod
                ? serviceMethod.call(this.config.service, params, body)
                : serviceMethod.call(this.config.service, params);

            serviceCall.subscribe({
                next: (response: any) => {
                    const transformedResponse = this.config.transformResponse
                        ? this.config.transformResponse(response)
                        : response;

                    const { hasError, data } = this.errorHandlerService.handleInternal(transformedResponse);
                    if (!hasError && data && data.items && data.items.length > 0) {
                        data.items.forEach((item: any) => {
                            const itemId = this.config.valueField ? item[this.config.valueField] : item.value || item.id;
                            const existingOption = this.options.find(option => option.value === itemId);
                            if (!existingOption) {
                                const newOption = {
                                    ...item,
                                    label: this.config.labelFormatter ? this.config.labelFormatter(item) : item.label || item.name,
                                    value: itemId
                                };
                                this.items.unshift(item);
                                this.options.unshift(newOption);
                            }
                        });
                    }
                    resolve();
                },
                error: (error: any) => {
                    console.error('Error loading specific items:', error);
                    this.loadData(true);
                    resolve();
                }
            });
        });
    }

    onValueChange(value: any) {
        this.value = value || [];
        this.onChange(this.value);
        this.onTouched();
    }

    /**
     * Public method to reload data programmatically
     */
    reloadData() {
        this.loadData(true);
    }

    /**
     * Public method to clear data and selection
     */
    clearData() {
        this.items = [];
        this.options = [];
        this.value = [];
        this.onChange([]);
    }

    // ControlValueAccessor implementation
    writeValue(value: any): void {
        const valueArray = Array.isArray(value) ? value : (value ? [value] : []);

        // If values are set and we don't have options loaded yet, or some values are not in current options
        if (valueArray.length > 0) {
            const missingValues = valueArray.filter(v => !this.options.find(option => option.value === v));
            if (missingValues.length > 0 || !this.options.length) {
                this.loadSpecificItems(valueArray).then(() => {
                    this.value = valueArray;
                    this.cd.markForCheck();
                });
            } else {
                this.value = valueArray;
            }
        } else {
            this.value = [];
        }
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    // Public methods
    reset() {
        this.items = [];
        this.options = [];
        this.offset = 0;
        this.filterValue = '';
        this.value = [];
    }

    reload() {
        if (this.isStaticMode) {
            this.loadStaticOptions();
        } else {
            this.loadData(true);
        }
    }

    /**
     * Get user initials for avatar display
     */
    getUserInitials(user: any): string {
        if (!user) return '?';
        const effectiveName = user.fullname || user.nickname || user.username || '';
        const words = effectiveName.trim().split(/\s+/);
        if (words.length >= 2) {
            // Get last 2 words
            const lastTwoWords = words.slice(-2);
            return (lastTwoWords[0][0] + lastTwoWords[1][0]).toUpperCase();
        }
        // If only one word, take first 2 characters
        return effectiveName.substring(0, 2).toUpperCase() || '?';
    }
}

